// 県ページの街見出しから開く全画面 dialog 地図。
//
// 設計: .spec/DESIGN-prefecture-map-dense-area.md §10
//
// 満たす規約:
//  1. **Leaflet は遅延ロード**。街ボタン押下まで CSS/JS もタイルも読まない。
//  2. **サーバ由来の文字列を HTML として解釈させない**。店名・ブランド名は data 属性の JSON から
//     読み、必ず textContent / setAttribute で置く。`L.divIcon({ html: ... })` へ渡す文字列は
//     num() を通した数値と固定の色だけで組み立てる。
//  3. **`_store_card` を変更しない**。入口はカード外の街見出しへ置く。
//  4. 位置情報はボタン押下時にだけ要求し、取得できてもビューは動かさない。
//  5. **街単位の地図（target-scope="location"）ではクラスタを出さない**。N 店なら必ず N ピン。
//     重なるピンだけをずらし、ずれた分は引き出し線と真位置のドットで示す（§15）。
//     配置の幾何は geo_map_declutter.mjs（純粋関数・Node でテスト）が決める。
import { computePlacements } from './geo_map_declutter.mjs';

(function () {
  'use strict';
  if (typeof document === 'undefined') return;

  var FIT_BOUNDS_MAX_ZOOM = 18;
  var MAX_EXPAND_STEPS = 4;
  var MOVE_TIMEOUT_MS = 1200;
  // **クラスタにまとめない距離の下限**。矩形は「ピン = アンカー（下端中央）から幅 ±28px・
  // 上へ 60px」「クラスタ = 中心から ±24px」で、支配的なのはピン対クラスタ。
  var MAX_CLUSTER_RADIUS = 105;
  // 実データの梅田にある「完全同一座標の 3 店」を、4px 以上の余白で展開する倍率。
  var SPIDERFY_DISTANCE_MULTIPLIER = 5.2;
  var CLUSTER_ICON_SIZE = 48;
  // spiderfy が収容と非交差を同時に満たせるかの判定に使う（§10-12-4）。
  // markercluster の円配置は脚長 R = FOOT_SEPARATION × multiplier × (2 + n) / 2π。
  var SPIDER_FOOT_SEPARATION = 25;
  // n がこれ以上だと markercluster は螺旋配置へ切り替わり（_circleSpiralSwitchover）、
  // multiplier での縮小が効かなくなるので spiderfy せず団子のまま返す。
  var SPIDER_SPIRAL_SWITCHOVER = 9;
  // markercluster が脚長へ課す下限（`legLength = Math.max(legLength, 35)`）。
  var SPIDER_MIN_LEG_LENGTH = 35;
  // markercluster は円の中心をハブより 10px 下げる（`center.y += 10`）。
  var SPIDER_CENTER_OFFSET_Y = 10;
  // ピンの最大寸法（gender ピン 56×60。アンカーは下端中央）。非交差・収容の余白計算に使う。
  var MARKER_MAX_WIDTH = 56;
  var MARKER_MAX_HEIGHT = 60;
  var MIN_MARKER_GAP = 4;
  // **街単位の地図のピン**。混雑数値を載せた通常ピン（56×60）のままだと、重なりを解くのに
  // 最低 60px 離す必要があり、街全体を 1 画面へ収めた縮尺では梅田で 470m ずれる
  // （z15 ＝ 1px≒3.9m）。数値をタップ時のポップアップとページ内の店舗リストへ逃がし、
  // ピンを 36×28 の小さいチップにすることで、ずれを梅田 126m / 渋谷 62m まで縮めた（§15-2）。
  // アンカーは**中心**（矢印を持たないので下端に合わせる意味が無い）。
  var COMPACT_PIN_WIDTH = 36;
  var COMPACT_PIN_HEIGHT = 28;
  // ずらす候補点の刻みと、これ以上は動かさないという上限。上限に当たったら黙って重ねず
  // `partial` を返して計測に残す。
  var DECLUTTER_STEP = 4;
  var DECLUTTER_MAX_RADIUS = 160;
  // 引き出し線を引く下限。数 px のずれで線を出すとノイズにしかならない。
  var LEADER_MIN_OFFSET = 6;
  // spiderfy 後のパンが終わるのを待つ上限（フレーム数）。無限待ちにしない。
  var SETTLE_MAX_FRAMES = 60;
  var ASSET_TIMEOUT_MS = 15000;
  var EDGE_PADDING_X = 36;
  var EDGE_PADDING_TOP = 72;
  var EDGE_PADDING_BOTTOM = 24;
  var SCROLL_LOCK_CLASS = 'geo-map-dialog-open';

  function axisOffset(min, max, low, high) {
    var over = Math.max(0, max - high);
    var under = Math.max(0, low - min);
    if (over > 0 && under === 0) return over;
    if (under > 0 && over === 0) return -under;

    return 0;
  }

  // 矩形の交差判定。spec 側の overlapping_pairs と同じ「両軸が gap 未満まで近づいたら交差」規約。
  function rectsCollide(a, b, gap) {
    return a.left - gap < b.right && b.left - gap < a.right &&
      a.top - gap < b.bottom && b.top - gap < a.bottom;
  }

  function rectInside(rect, area, gap) {
    return rect.left >= area.left + gap && rect.right <= area.right - gap &&
      rect.top >= area.top + gap && rect.bottom <= area.bottom - gap;
  }

  function num(value) {
    var parsed = Number(value);
    return isFinite(parsed) ? Math.round(parsed) : 0;
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function pushDataLayer(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  // **URL 単位で Promise を共有する**。失敗後に再試行したとき、同じ URL の link/script を
  // 二重に足さない（成功したものはそのまま使い回し、失敗したものだけ捨てて引き直す）。
  var assetPromises = {};

  function withTimeout(promise, label) {
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () { reject(new Error('timeout: ' + label)); }, ASSET_TIMEOUT_MS);
      promise.then(function (value) { clearTimeout(timer); resolve(value); },
        function (error) { clearTimeout(timer); reject(error); });
    });
  }

  function loadOnce(source, factory) {
    var key = source.url;
    if (assetPromises[key]) return assetPromises[key];

    assetPromises[key] = withTimeout(new Promise(factory), key).catch(function (error) {
      delete assetPromises[key];
      throw error;
    });
    return assetPromises[key];
  }

  function loadStyle(source) {
    return loadOnce(source, function (resolve, reject) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      if (source.integrity) {
        link.integrity = source.integrity;
        link.crossOrigin = '';
      }
      link.onload = function () { resolve(); };
      link.onerror = function () {
        if (link.parentNode) link.parentNode.removeChild(link);
        reject(new Error('failed to load stylesheet'));
      };
      link.href = source.url;
      document.head.appendChild(link);
    });
  }

  function loadScript(source) {
    return loadOnce(source, function (resolve, reject) {
      var script = document.createElement('script');
      if (source.integrity) {
        script.integrity = source.integrity;
        script.crossOrigin = '';
      }
      script.async = false;
      script.onload = function () { resolve(); };
      script.onerror = function () {
        if (script.parentNode) script.parentNode.removeChild(script);
        reject(new Error('failed to load script'));
      };
      script.src = source.url;
      document.head.appendChild(script);
    });
  }

  // leaflet.markercluster は L が定義済みであることを要求するので **スクリプトは直列**に読む。
  function loadAssets(assets) {
    return Promise.resolve().then(function () {
      var stylesLoaded = Promise.all(assets.styles.map(loadStyle));
      var scriptsLoaded = assets.scripts.reduce(function (chain, source) {
        return chain.then(function () { return loadScript(source); });
      }, Promise.resolve());

      return Promise.all([stylesLoaded, scriptsLoaded]);
    });
  }

  // data 属性が壊れている／改竄されているときに、空の地図を「正常」として出さない。
  function validBranches(value) {
    if (!Array.isArray(value) || value.length === 0) return null;

    var seen = {};
    var ok = value.every(function (branch) {
      if (!branch || typeof branch !== 'object') return false;
      if (typeof branch.lat !== 'number' || !isFinite(branch.lat) || Math.abs(branch.lat) > 90) return false;
      if (typeof branch.lng !== 'number' || !isFinite(branch.lng) || Math.abs(branch.lng) > 180) return false;
      if (typeof branch.id !== 'number' || !Number.isInteger(branch.id) || branch.id <= 0) return false;
      if (typeof branch.location_id !== 'number' || !Number.isInteger(branch.location_id) || branch.location_id <= 0) return false;
      if (typeof branch.name !== 'string') return false;
      if (typeof branch.url !== 'string' || branch.url.length === 0) return false;
      if (seen[branch.id]) return false;

      seen[branch.id] = true;
      return true;
    });
    return ok ? value : null;
  }

  function validAssets(value) {
    if (!value || typeof value !== 'object') return null;
    if (!Array.isArray(value.styles) || !Array.isArray(value.scripts) || value.scripts.length === 0) return null;

    var ok = value.styles.concat(value.scripts).every(function (source) {
      return source && typeof source.url === 'string' && source.url.length > 0;
    });
    if (!ok) return null;
    if (typeof value.tile_url !== 'string' || value.tile_url.length === 0) return null;
    // 出典は**欠けたまま出荷させない**。地理院タイルは出所明示が利用条件なので、
    // helper が配り忘れた状態で地図だけ描けてしまう経路を作らない。
    if (!value.tile_options || typeof value.tile_options.attribution !== 'string' ||
        value.tile_options.attribution.length === 0) return null;

    return value;
  }

  function parseJson(value, fallback) {
    if (!value) return fallback;
    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  }

  function el(tag, css, text) {
    var node = document.createElement(tag);
    if (css) node.style.cssText = css;
    if (text != null) node.textContent = text;
    return node;
  }

  // ピンの中身。**num() を通した数値と固定文字列しか連結しない**。
  function pinHtml(branch) {
    var offline = !!branch.status_label;
    var background = offline ? '#94a3b8' : '#e94560';
    var inner = '';
    var gender = false;

    if (offline) {
      inner = '<span class="map-pin__label">－</span>';
    } else if (branch.is_number) {
      gender = true;
      inner = ratioBarHtml(num(branch.men), num(branch.women)) +
        '<span class="map-pin__nums">♂' + num(branch.men) + ' ♀' + num(branch.women) + '</span>';
    } else if (branch.is_percent_gender) {
      gender = true;
      inner = ratioBarHtml(num(branch.percent_men), num(branch.percent_women)) +
        '<span class="map-pin__nums">♂' + num(branch.percent_men) + '% ♀' + num(branch.percent_women) + '%</span>';
    } else if (branch.is_percent_total) {
      inner = '<span class="map-pin__label">' + num(branch.percent_total) + '%</span>';
    } else {
      inner = '<span class="map-pin__label">-</span>';
    }

    return '<div class="map-pin geo-map__pin' + (gender ? ' map-pin--gender' : '') +
      '" style="background:' + background + '">' + inner +
      '<div class="map-pin__arrow" style="border-top-color:' + background + '"></div></div>';
  }

  // 街単位の地図で使う小さいピン。**出す数値は「女性側の実値」だけ**にする。
  //
  // 36×28 に男女の両方は入らない。かといって男女から合成した 1 つの数（平均など）を作ると、
  // どのデータにも存在しない値を地図に書くことになる（本プロジェクトの「表示する各値が対象
  // データ由来か」の規約違反）。女性側は 3 データモードすべてに実値が存在し、かつこのサイトで
  // 最もよく見られている指標なので、そのまま出せる。男女の内訳はタップでポップアップに出る。
  //
  // divIcon へ渡すのは **num() を通した数値と固定の色だけ**（店名は入れない＝規約 2）。
  function compactPinHtml(branch) {
    var offline = !!branch.status_label;
    var background = offline ? '#94a3b8' : '#e94560';
    var label;

    if (offline) {
      label = '－';
    } else if (branch.is_number) {
      label = '♀' + num(branch.women);
    } else if (branch.is_percent_gender) {
      label = '♀' + num(branch.percent_women) + '%';
    } else if (branch.is_percent_total) {
      label = num(branch.percent_total) + '%';
    } else {
      label = '-';
    }

    return '<div class="geo-map__chip" style="background:' + background + '">' +
      '<span class="geo-map__chip-label">' + label + '</span></div>';
  }

  function ratioBarHtml(men, women) {
    var total = men + women;
    var percent = total > 0 ? Math.round((men / total) * 100) : 0;
    return '<span class="map-pin__bar"><span class="map-pin__bar-men" style="width:' + percent + '%"></span></span>';
  }

  function accessibleName(branch) {
    if (branch.status_label) return branch.name + '（' + branch.status_label + '）';
    if (branch.is_number) return branch.name + '（男性' + num(branch.men) + '人 女性' + num(branch.women) + '人）';
    if (branch.is_percent_gender) {
      return branch.name + '（男性' + num(branch.percent_men) + 'パーセント 女性' + num(branch.percent_women) + 'パーセント）';
    }
    if (branch.is_percent_total) return branch.name + '（全体' + num(branch.percent_total) + 'パーセント）';
    return branch.name;
  }

  function popupBarNode(men, women) {
    var total = men + women;
    if (total <= 0) return null;
    var bar = el('div');
    bar.className = 'map-popup__bar';
    var fill = el('span');
    fill.className = 'map-popup__bar-men';
    fill.style.width = Math.round((men / total) * 100) + '%';
    bar.appendChild(fill);
    return bar;
  }

  function popupStatusNode(branch) {
    var wrap = el('div', 'font-size:14px;margin-bottom:10px;line-height:1.6');
    if (branch.status_label) {
      wrap.textContent = branch.status_label;
      return wrap;
    }

    var men;
    var women;
    var unit;
    if (branch.is_number) {
      men = num(branch.men); women = num(branch.women); unit = '人';
    } else if (branch.is_percent_gender) {
      men = num(branch.percent_men); women = num(branch.percent_women); unit = '%';
    } else {
      if (branch.is_percent_total) wrap.textContent = '全体 ' + num(branch.percent_total) + '%';
      return wrap;
    }

    var bar = popupBarNode(men, women);
    if (bar) wrap.appendChild(bar);
    wrap.appendChild(el('span', 'color:#0d47a1', '♂ ' + men + unit));
    wrap.appendChild(document.createTextNode(' / '));
    wrap.appendChild(el('span', 'color:#880e4f', '♀ ' + women + unit));
    return wrap;
  }

  function popupNode(branch) {
    var root = el('div', 'min-width:200px;font-family:sans-serif');
    root.appendChild(el('div', 'font-size:11px;color:#64748b;margin-bottom:2px', branch.store_name));
    root.appendChild(el('div', 'font-size:15px;font-weight:700;margin-bottom:8px;color:#1e293b', branch.name));
    root.appendChild(popupStatusNode(branch));

    if (branch.entry_notice_label) {
      var notice = el('div', null, branch.entry_notice_label);
      notice.className = 'entry-notice-badge entry-notice-badge--popup';
      notice.setAttribute('data-entry-notice-badge', '');
      root.appendChild(notice);
    }

    var link = el('a', null, '詳細を見る');
    link.className = 'geo-map__popup-link';
    link.setAttribute('href', branch.url);
    link.setAttribute('data-gtm-event', 'location_map_card_link_click');
    link.setAttribute('data-gtm-branch-id', String(num(branch.id)));
    root.appendChild(link);
    return root;
  }

  function GeoMap(dialog) {
    this.root = dialog;
    this.dialog = dialog;
    this.canvas = dialog.querySelector('[data-geo-map-canvas]');
    this.controls = dialog.querySelector('[data-geo-map-controls]');
    this.status = dialog.querySelector('[data-geo-map-status]');
    this.title = dialog.querySelector('[data-geo-map-title]');
    // 面のラベル（「東京都」「関東」「新宿」）と、その面の種別。
    // **パラメータ名を `prefecture` にしない**。地図は県ページ以外（エリア・街）にも出るので、
    // 名前に県を焼き込むと面別の集計ができなくなる（§13）。
    this.scope = dialog.getAttribute('data-geo-map-scope') || '';
    this.surfaceType = dialog.getAttribute('data-geo-map-surface-type') || '';
    this.branches = validBranches(parseJson(dialog.getAttribute('data-geo-map-branches'), null));
    this.assets = validAssets(parseJson(dialog.getAttribute('data-geo-map-assets'), null));
    this.byId = {};
    this.markers = {};
    this.loading = null;
    this.map = null;
    this.clusterGroup = null;
    this.pinLayer = null;
    this.decorLayer = null;
    // 今この地図に載っているマーカー。zoomend / resize のハンドラは常にここを読むので、
    // 閉じたあとに遅れて届いた発火は空振りする（前の街の集合に対して置き直さない）。
    this.mountedMarkers = [];
    this.activeLocationId = null;
    this.activeTargetScope = 'location';
    this.activeTargetLabel = '';
    this.locationMarker = null;
    this.opener = null;
    this.scrollPosition = { x: 0, y: 0 };
    this.openGeneration = 0;
    this.expandGeneration = 0;
    this.historyEntryActive = false;
    this.historyBackPending = false;
    this.pendingNavigationUrl = null;
    this.pendingNavigationTimer = null;
    this.historyKey = 'geo-map-' + String(Date.now()) + '-' + String(Math.random()).slice(2);

    var self = this;
    (this.branches || []).forEach(function (branch) { self.byId[num(branch.id)] = branch; });
  }

  GeoMap.prototype.usable = function () {
    return !!(this.branches && this.assets && this.canvas && typeof this.dialog.showModal === 'function');
  };

  GeoMap.prototype.announce = function (message) {
    if (this.status) this.status.textContent = message;
  };

  GeoMap.prototype.locationButtons = function () {
    return document.querySelectorAll('[data-geo-map-location-button]');
  };

  GeoMap.prototype.branchesForLocation = function (locationId, targetScope) {
    if (targetScope === 'prefecture') return this.branches.slice();

    var id = num(locationId);
    return this.branches.filter(function (branch) { return num(branch.location_id) === id; });
  };

  GeoMap.prototype.markersForLocation = function (locationId, targetScope) {
    var self = this;
    return this.branchesForLocation(locationId, targetScope).map(function (branch) {
      return self.markers[num(branch.id)];
    }).filter(function (marker) { return !!marker; });
  };

  GeoMap.prototype.setButtonLoading = function (button, loading) {
    if (!button) return;
    if (!button.getAttribute('data-geo-map-original-label')) {
      button.setAttribute('data-geo-map-original-label', button.textContent);
    }
    button.disabled = loading;
    button.textContent = loading
      ? '地図を読み込んでいます…'
      : button.getAttribute('data-geo-map-original-label');
  };

  GeoMap.prototype.lockBackground = function () {
    document.documentElement.classList.add(SCROLL_LOCK_CLASS);
    if (document.body) document.body.classList.add(SCROLL_LOCK_CLASS);
  };

  GeoMap.prototype.unlockBackground = function () {
    document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
    if (document.body) document.body.classList.remove(SCROLL_LOCK_CLASS);
  };

  GeoMap.prototype.ownsCurrentHistoryState = function () {
    var state = window.history.state;
    return !!(state && state.geoMapDialog === true && state.geoMapDialogKey === this.historyKey);
  };

  GeoMap.prototype.pushHistory = function () {
    window.history.pushState({ geoMapDialog: true, geoMapDialogKey: this.historyKey }, '');
    this.historyEntryActive = true;
  };

  GeoMap.prototype.resetMap = function () {
    if (this.map) {
      try {
        this.map.remove();
      } catch (_error) {
        // remove 自体が失敗しても、次回 L.map が同じ canvas を再利用できる状態へ戻す。
      }
    }
    this.map = null;
    this.clusterGroup = null;
    this.pinLayer = null;
    this.decorLayer = null;
    this.mountedMarkers = [];
    this.markers = {};
    this.locationMarker = null;
    if (this.canvas) {
      this.canvas.innerHTML = '';
      if (this.canvas._leaflet_id) delete this.canvas._leaflet_id;
    }
  };

  GeoMap.prototype.failOpen = function (error) {
    this.resetMap();
    if (this.controls) this.controls.hidden = true;
    this.announce('地図を読み込めませんでした。時間をおいて試してください。');
    if (window.console && typeof window.console.error === 'function') {
      window.console.error('geo-map failed', error);
    }
  };

  GeoMap.prototype.finishNavigationFromDialog = function (url, replace) {
    this.openGeneration += 1;
    this.expandGeneration += 1;
    // 遅れて届く zoomend / resize が、閉じた地図のマーカー集合を置き直さないようにする。
    this.mountedMarkers = [];
    if (this.dialog.open) this.dialog.close();
    this.unlockBackground();
    this.setButtonLoading(this.opener, false);
    this.historyEntryActive = false;
    this.historyBackPending = false;
    if (replace) {
      window.location.replace(url);
    } else {
      window.location.assign(url);
    }
  };

  GeoMap.prototype.navigateFromPopup = function (link, event) {
    if (!link || !link.href || !this.historyEntryActive || !this.ownsCurrentHistoryState()) return;

    event.preventDefault();
    this.pendingNavigationUrl = link.href;
    this.historyBackPending = true;
    window.history.back();

    var self = this;
    clearTimeout(this.pendingNavigationTimer);
    this.pendingNavigationTimer = setTimeout(function () {
      if (!self.pendingNavigationUrl) return;
      var url = self.pendingNavigationUrl;
      self.pendingNavigationUrl = null;
      // popstate が来ない環境では、dialog 用 entry 自体を詳細 URL で置換する。
      self.finishNavigationFromDialog(url, true);
    }, 1000);
  };

  GeoMap.prototype.closeDialog = function (fromPopState) {
    var shouldGoBack = !fromPopState && this.historyEntryActive && this.ownsCurrentHistoryState();
    var opener = this.opener;
    var scrollPosition = this.scrollPosition;

    this.openGeneration += 1;
    this.expandGeneration += 1;
    if (this.dialog.open) this.dialog.close();
    this.unlockBackground();
    this.setButtonLoading(opener, false);
    this.historyEntryActive = false;

    if (shouldGoBack) {
      // close ボタン / Esc が積んだ state だけを戻す。次の popstate は自分で起こしたものなので無視する。
      this.historyBackPending = true;
      window.history.back();
    } else if (fromPopState) {
      this.historyBackPending = false;
    }

    if (opener && typeof opener.focus === 'function') {
      window.requestAnimationFrame(function () {
        try {
          opener.focus({ preventScroll: true });
        } catch (_error) {
          opener.focus();
        }
        window.scrollTo(scrollPosition.x, scrollPosition.y);
      });
    }
  };

  // 何度開いてもアセット読込は 1 回だけ。地図自体は dialog が開いているときに render する。
  GeoMap.prototype.ensureAssets = function () {
    if (this.loading) return this.loading;

    var self = this;
    this.loading = loadAssets(this.assets).then(function () {
      if (typeof window.L === 'undefined' || typeof window.L.markerClusterGroup !== 'function') {
        throw new Error('leaflet is unavailable');
      }
    }).catch(function (error) {
      self.loading = null;
      throw error;
    });

    return this.loading;
  };

  GeoMap.prototype.render = function () {
    var self = this;
    var L = window.L;
    var animate = !reducedMotion();
    var initial = this.branchesForLocation(this.activeLocationId, this.activeTargetScope)[0] || this.branches[0];

    this.canvas.setAttribute('role', 'application');
    this.canvas.setAttribute('aria-labelledby', 'geo-map-title');
    this.canvas.setAttribute('aria-describedby', 'geo-map-status');

    this.map = L.map(this.canvas, {
      zoomAnimation: animate,
      markerZoomAnimation: animate,
      fadeAnimation: animate,
      dragging: true,
      scrollWheelZoom: false
    });
    // L.map 直後に保持しておけば、以降の setView / tileLayer が投げても確実に remove できる。
    this.map.setView([initial.lat, initial.lng], FIT_BOUNDS_MAX_ZOOM, { animate: false });
    if (!animate) this.disablePanAnimation();

    // タイルの出所・出典・ズーム範囲は **すべて helper（geo_map_asset_sources）から来る**。
    // ここで直書きすると、提供元を差し替えたときに面ごとに値が食い違う（#334 と同じ轍）。
    L.tileLayer(this.assets.tile_url, this.assets.tile_options).addTo(this.map);

    // **街単位の地図（location scope）はクラスタ経路を丸ごと使わない**。
    // 面ごとに scope は排他（`geo_map_prefecture_button?` は街ボタンが 1 つも無いときだけ真）なので、
    // ここで 1 回決め打ちすればよく、開くたびのレイヤ付け替えは要らない（§15-3）。
    if (this.declutterScope()) {
      this.renderPlainLayers();
      this.publishBounds();
      this.bindCanvasKeyboard();
      return;
    }

    this.clusterGroup = L.markerClusterGroup({
      showCoverageOnHover: false,
      // **false**。true だと markercluster 自身が clusterclick/keypress を掴み
      // （`_zoomOrSpiderfy`）、最大 zoom の同一座標クラスタを**収容検査なしで** spiderfy する。
      // `clustered` はユーザーにタップを促す状態なので、その入口で検査を迂回されると
      // spiderCanFit を入れた意味が無くなる（r2 W6 / r1 W7）。展開は expandCluster へ一本化する。
      spiderfyOnMaxZoom: false,
      zoomToBoundsOnClick: false,
      maxClusterRadius: MAX_CLUSTER_RADIUS,
      spiderfyDistanceMultiplier: SPIDERFY_DISTANCE_MULTIPLIER,
      // reduced-motion でも false にしない。markercluster の非アニメーション経路は
      // zoomToShowLayer / spiderfy の完了通知を壊す。
      animate: true,
      iconCreateFunction: function (cluster) {
        return L.divIcon({
          className: 'geo-map__cluster-icon',
          html: '<div class="geo-map__cluster"><span>' + num(cluster.getChildCount()) + '</span></div>',
          iconSize: [CLUSTER_ICON_SIZE, CLUSTER_ICON_SIZE],
          iconAnchor: [CLUSTER_ICON_SIZE / 2, CLUSTER_ICON_SIZE / 2]
        });
      }
    });

    this.branches.forEach(function (branch) {
      var marker = self.buildMarker(branch);
      if (!marker) return;
      self.markers[num(branch.id)] = marker;
      self.clusterGroup.addLayer(marker);
    });
    this.map.addLayer(this.clusterGroup);

    this.clusterGroup.on('clusterclick', function (event) { self.expandCluster(event.layer); });
    // zoomToBoundsOnClick:false は Enter の既定経路も止めるため、同じ有限展開へ繋ぎ直す。
    this.clusterGroup.on('clusterkeypress', function (event) {
      var original = event.originalEvent || {};
      if (original.key !== 'Enter' && original.keyCode !== 13) return;
      self.expandCluster(event.layer);
    });
    this.clusterGroup.on('animationend', function () { self.labelClusters(); });
    // 'spiderfied' は `{ cluster, markers }` を渡す。寄せ先の計算には**その markers だけ**を使う。
    this.clusterGroup.on('spiderfied', function (event) {
      self.labelClusters();
      self.panSpiderIntoView(event.markers);
    });
    this.labelClusters();

    // 収容（街の全店が視界の内側にあること）は描画済みマーカーの DOM だけでは確かめられない。
    // 現在の視界を data 属性で出す（data-geo-map-fit-zoom と同じ既存パターン。window は汚さない）。
    this.map.on('moveend', function () { self.publishBounds(); });
    this.publishBounds();

    this.bindCanvasKeyboard();
  };

  // 街単位の地図か。ここが真なら markercluster は一切使わず、全店を個別のピンで置く。
  GeoMap.prototype.declutterScope = function () {
    return this.activeTargetScope !== 'prefecture';
  };

  // クラスタを使わない面のレイヤ。ピンと装飾（引き出し線・真位置ドット）を分けておくと、
  // 再配置のたびに装飾だけを捨てて引き直せる。
  GeoMap.prototype.renderPlainLayers = function () {
    var self = this;
    var L = window.L;

    // **null にする**。moveAndWait は `if (group)` で markercluster のアニメーション完了を
    // 待つので、使わない面では空の group を渡さず素通りさせる。
    this.clusterGroup = null;
    this.pinLayer = L.layerGroup().addTo(this.map);
    this.decorLayer = L.layerGroup().addTo(this.map);

    this.branches.forEach(function (branch) {
      var marker = self.buildMarker(branch, true);
      if (!marker) return;
      self.markers[num(branch.id)] = marker;
    });

    this.map.on('moveend', function () { self.publishBounds(); });
    // ズームで画素距離が変わるので置き直す。パンでは相対関係が変わらないので不要。
    // 端末の回転・dialog の invalidateSize は zoomend を出さないので resize も拾う。
    this.map.on('zoomend', function () { self.declutter(); });
    this.map.on('resize', function () { self.declutter(); });
  };

  // 対象の街のマーカーだけを地図へ載せる。**載せ替えの前に必ず全部降ろす**
  // （前の街のピンが残ると、対象外の店が重なり判定に混ざる）。
  GeoMap.prototype.mountMarkers = function (targets) {
    if (!this.pinLayer) return;

    this.pinLayer.clearLayers();
    if (this.decorLayer) this.decorLayer.clearLayers();
    this.mountedMarkers = targets.slice();
    var layer = this.pinLayer;
    targets.forEach(function (marker) { layer.addLayer(marker); });
  };

  // 重なるピンをずらして置き直す。**毎回「真の座標」から計算し直す**（前回のずれを足さない）。
  // 返す値は expandMarkers と同じ語彙: 動かさずに済めば 'separated'、動かしたら 'displaced'、
  // 枠内に置き場所が無い店が出たら 'partial'。
  GeoMap.prototype.declutter = function () {
    var self = this;
    var L = window.L;
    var markers = this.mountedMarkers || [];
    if (!this.map || !markers.length) return 'separated';

    var box = this.canvas.getBoundingClientRect();
    var points = markers.map(function (marker) {
      var point = self.map.latLngToContainerPoint(marker.geoMapTrueLatLng);
      return { id: marker.geoMapBranchId, x: Math.round(point.x), y: Math.round(point.y) };
    });
    // 固定 UI（タイトル・閉じる・現在地ボタン・ズームコントロール）はビューポート座標で
    // 返るので、canvas 原点へ寄せてから渡す。
    var obstacles = this.overlayRects().map(function (rect) {
      return {
        left: rect.left - box.left, right: rect.right - box.left,
        top: rect.top - box.top, bottom: rect.bottom - box.top
      };
    });

    var placements = computePlacements(points, {
      footprint: {
        left: -COMPACT_PIN_WIDTH / 2, right: COMPACT_PIN_WIDTH / 2,
        top: -COMPACT_PIN_HEIGHT / 2, bottom: COMPACT_PIN_HEIGHT / 2
      },
      gap: MIN_MARKER_GAP,
      area: { left: 0, top: 0, right: box.width, bottom: box.height },
      obstacles: obstacles,
      step: DECLUTTER_STEP,
      maxRadius: DECLUTTER_MAX_RADIUS
    });

    if (this.decorLayer) this.decorLayer.clearLayers();
    var displaced = 0;
    var unplaced = 0;

    placements.forEach(function (placement, index) {
      var marker = markers[index];
      self.applyOffset(marker, placement.dx, placement.dy);
      if (!placement.placed) unplaced += 1;
      if (!placement.displaced) return;

      displaced += 1;
      if (Math.abs(placement.dx) < LEADER_MIN_OFFSET && Math.abs(placement.dy) < LEADER_MIN_OFFSET) return;
      var to = self.map.containerPointToLatLng(L.point(placement.x, placement.y));
      // 装飾。**タップも読み上げも取らない**（interactive:false＝Leaflet 側で pointer-events を切る）。
      L.polyline([marker.geoMapTrueLatLng, to], {
        className: 'geo-map__leader', color: '#64748b', weight: 1, opacity: 0.85, interactive: false
      }).addTo(self.decorLayer);
      L.circleMarker(marker.geoMapTrueLatLng, {
        className: 'geo-map__anchor-dot', radius: 3, weight: 1,
        color: '#ffffff', fillColor: '#64748b', fillOpacity: 1, interactive: false
      }).addTo(self.decorLayer);
    });

    this.root.setAttribute('data-geo-map-displaced', String(displaced));
    if (unplaced) return 'partial';

    return displaced ? 'displaced' : 'separated';
  };

  // 変位の適用は **`setLatLng` ではなくアイコンの margin**。
  // Leaflet は iconAnchor を marginLeft / marginTop として当てている（Icon#_setIconStyles）ので、
  // そこへ変位を足すだけで画素単位に動かせる。`setLatLng` を使うと
  // `getLatLng()` が真の座標でなくなり、markercluster の再クラスタリング・popup の autoPan・
  // `_preSpiderfyLatlng` を読む既存コードを道連れにする（§15-4）。
  GeoMap.prototype.applyOffset = function (marker, dx, dy) {
    marker.geoMapOffset = { dx: dx, dy: dy };

    // ポップアップは marker の latlng（＝真の座標）を基準に開くので、変位ぶんを offset で足して
    // ピン本体へ追随させる。足さないと、ずらしたピンをタップしたのに吹き出しだけ離れた
    // 真の位置に出る（線の先に出るのは理屈は通るが、タップしたものと繋がって見えない）。
    var popup = typeof marker.getPopup === 'function' ? marker.getPopup() : null;
    if (popup) popup.options.offset = window.L.point(dx, dy);

    var element = typeof marker.getElement === 'function' ? marker.getElement() : null;
    if (!element) return;

    var anchor = marker.geoMapAnchor || [0, 0];
    element.style.marginLeft = (-anchor[0] + dx) + 'px';
    element.style.marginTop = (-anchor[1] + dy) + 'px';
  };

  // 街単位の地図の展開。収容ビュー（全店が入る最大ズーム）を作ってから置き直すだけで、
  // クラスタを割るためのズームループも spiderfy も要らない。
  GeoMap.prototype.placeMarkers = function (targets, onInitialFit) {
    var self = this;
    var L = window.L;
    var generation = ++this.expandGeneration;

    this.mountMarkers(targets);

    var bounds = L.latLngBounds(targets.map(function (marker) { return marker.geoMapTrueLatLng; }));
    this.root.setAttribute('data-geo-map-expand-steps', '0');
    this.root.removeAttribute('data-geo-map-bounds');

    return this.moveAndWait(function () {
      self.map.fitBounds(bounds, self.fitPadding({ maxZoom: FIT_BOUNDS_MAX_ZOOM }));
    }).then(function () {
      if (generation !== self.expandGeneration) return null;

      self.root.setAttribute('data-geo-map-fit-zoom', String(self.map.getZoom()));
      if (onInitialFit) onInitialFit();

      return self.declutter();
    });
  };

  // Leaflet はマーカーの Enter を click に変換するが Space は扱わない。
  GeoMap.prototype.bindCanvasKeyboard = function () {
    this.canvas.addEventListener('keydown', function (event) {
      if (event.key !== ' ' && event.key !== 'Spacebar') return;
      var icon = event.target.closest && event.target.closest('.leaflet-marker-icon');
      if (!icon) return;
      event.preventDefault();
      icon.click();
    });
  };

  GeoMap.prototype.publishBounds = function () {
    if (!this.map) return;
    var bounds = this.map.getBounds();
    this.root.setAttribute('data-geo-map-bounds', [
      bounds.getSouth(), bounds.getWest(), bounds.getNorth(), bounds.getEast()
    ].map(function (value) { return value.toFixed(6); }).join(','));
  };

  GeoMap.prototype.disablePanAnimation = function () {
    var native = this.map.panBy;
    this.map.panBy = function (offset, options) {
      var forced = window.L.Util.extend({}, options || {}, { animate: false });
      return native.call(this, offset, forced);
    };
  };

  GeoMap.prototype.fitPadding = function (extra) {
    var options = {
      paddingTopLeft: [EDGE_PADDING_X, EDGE_PADDING_TOP],
      paddingBottomRight: [EDGE_PADDING_X, EDGE_PADDING_BOTTOM]
    };
    if (extra && extra.maxZoom) options.maxZoom = extra.maxZoom;
    return options;
  };

  // action が起こした movestart の後の moveend だけを待つ。前段アニメーションのキャンセル由来
  // moveend を拾って、移動中の DOM を収束判定へ渡さない。
  GeoMap.prototype.moveAndWait = function (action) {
    var map = this.map;
    var group = this.clusterGroup;
    return new Promise(function (resolve, reject) {
      var finished = false;
      var actionInvoked = false;
      var moveStarted = false;
      var moveEnded = false;
      var finish = function () {
        if (finished) return;
        finished = true;
        map.off('movestart', onMoveStart);
        map.off('moveend', onMoveEnd);
        if (group) group.off('animationend', onClusterAnimationEnd);
        clearTimeout(timer);
        resolve();
      };
      var onMoveStart = function () {
        if (actionInvoked) moveStarted = true;
      };
      var onMoveEnd = function () {
        if (!moveStarted) return;
        moveEnded = true;
        if (!group || group._inZoomAnimation === 0) finish();
      };
      var onClusterAnimationEnd = function () {
        if (moveEnded && group._inZoomAnimation === 0) finish();
      };
      var timer = setTimeout(finish, MOVE_TIMEOUT_MS);
      map.on('movestart', onMoveStart);
      map.on('moveend', onMoveEnd);
      if (group) group.on('animationend', onClusterAnimationEnd);
      try {
        actionInvoked = true;
        action();
      } catch (error) {
        map.off('movestart', onMoveStart);
        map.off('moveend', onMoveEnd);
        if (group) group.off('animationend', onClusterAnimationEnd);
        clearTimeout(timer);
        reject(error);
      }
    });
  };

  // 対象マーカーを覆っているクラスタ集合と、**視界の外にある対象の数**を返す。
  // 収容の判定に getVisibleParent の null を使ってはいけない（r1 C2）。markercluster が
  // マーカーを DOM から外す境界は `getBounds().pad(1)`（mobile でも `getBounds()` ちょうど）で、
  // 「1 画面ぶん外へ出る」まで null にならず、数十 px のパンでは反応しないため。
  // 収容は地図の bounds で直接見る。spec の expect_targets_in_view と同じ不変条件になる。
  GeoMap.prototype.clustersCoveringTargets = function (targets) {
    var clusters = [];
    var offscreen = 0;
    var group = this.clusterGroup;
    var bounds = this.map.getBounds();
    targets.forEach(function (marker) {
      // spiderfy 中の子は脚の座標を持つので、店舗の実座標へ戻して判定する。
      var latLng = marker._preSpiderfyLatlng || marker.getLatLng();
      var parent = group.getVisibleParent(marker);
      if (!parent || !bounds.contains(latLng)) offscreen += 1;
      if (!parent || parent === marker || typeof parent.getAllChildMarkers !== 'function') return;
      if (clusters.indexOf(parent) === -1) clusters.push(parent);
    });
    return { clusters: clusters, offscreen: offscreen };
  };

  // 実座標で比べる。spiderfy 中の子は脚の座標を持つので、`getLatLng()` を直読みすると
  // 同一座標クラスタを「別座標」と誤判定する（規約「実座標を使う」の 3 つ目の適用点＝r2 W2）。
  GeoMap.prototype.samePoint = function (markers) {
    if (!markers.length) return false;
    var first = markers[0]._preSpiderfyLatlng || markers[0].getLatLng();
    return markers.every(function (marker) {
      return (marker._preSpiderfyLatlng || marker.getLatLng()).equals(first);
    });
  };

  // spiderfy() は DOM 展開を非同期で行うため、呼び出しただけで成功扱いにしない。
  // 完了通知（またはタイムアウト）後に対象 marker の visible parent を再検査する。
  GeoMap.prototype.spiderfyAndWait = function (cluster, targets) {
    var self = this;
    return new Promise(function (resolve, reject) {
      var finished = false;
      var group = self.clusterGroup;
      var finish = function () {
        if (finished) return;
        finished = true;
        group.off('spiderfied', onSpiderfied);
        clearTimeout(hardTimer);
        clearTimeout(timer);
        // パン（panSpiderIntoView）は 'spiderfied' の直後に走る。rAF 1 回で読むと
        // **パンの途中の getBounds()** を見て違反を見逃すので、パンの終了を待つ（r2 W1）。
        var settleStartedAt = 0;
        var settle = function (frames) {
          if (self.panning && frames < SETTLE_MAX_FRAMES) {
            window.requestAnimationFrame(function () { settle(frames + 1); });
            return;
          }
          var state = self.clustersCoveringTargets(targets);
          resolve(state.clusters.length === 0 && state.offscreen === 0);
        };
        window.requestAnimationFrame(function () { settle(settleStartedAt); });
      };
      var onSpiderfied = function (event) {
        if (event && event.cluster && event.cluster !== cluster) return;
        finish();
      };
      // **spiderfy を実際に呼んでから**完了待ちの時計を回す。アニメーション待ちと同じ時計を
      // 共有すると、混んでいるときに「まだ呼んでいないのに時間切れ」で partial を申告し、
      // 直後に spiderfy が走る（間欠的にクラスタが残って見える正体）。hardTimer は保険。
      var timer = null;
      var hardTimer = setTimeout(finish, MOVE_TIMEOUT_MS * 2);

      group.on('spiderfied', onSpiderfied);
      // 既に展開済みのクラスタに spiderfy() を呼んでも無言で return する。イベントを待つと
      // 必ず timeout してしまうので、その場で状態を見て返す。
      if (group._spiderfied === cluster) {
        finish();
        return;
      }
      // markercluster の `spiderfy()` は **ズームアニメーション中だと無言で return する**
      // （`_spiderfied === this || _inZoomAnimation` で早期 return）。クリック直後に呼ぶと
      // 展開されないまま MOVE_TIMEOUT_MS で打ち切られ、`partial` を申告してしまう
      // （クラスタ直接操作の間欠失敗の正体）。アニメーションが終わってから呼ぶ。
      var invoke = function (frames) {
        if (finished) return;
        if (group._inZoomAnimation && frames < SETTLE_MAX_FRAMES) {
          window.requestAnimationFrame(function () { invoke(frames + 1); });
          return;
        }
        timer = setTimeout(finish, MOVE_TIMEOUT_MS);
        try {
          cluster.spiderfy();
        } catch (error) {
          finished = true;
          group.off('spiderfied', onSpiderfied);
          clearTimeout(hardTimer);
          clearTimeout(timer);
          reject(error);
        }
      };
      invoke(0);
    });
  };

  // 対象がすべて収まる最大 zoom。fitBounds が内部で使うのと同じ padding（左上＋右下の合算）を
  // 渡すので、「この zoom までなら fitBounds は収容を保てる」を先に判定できる。
  GeoMap.prototype.containableZoom = function (bounds) {
    var L = window.L;
    return this.map.getBoundsZoom(bounds, false,
      L.point(EDGE_PADDING_X * 2, EDGE_PADDING_TOP + EDGE_PADDING_BOTTOM));
  };

  // 脚のピンの寸法。gender ピン 56×60 / 非 gender 48×52 が混在するので、**実際の icon の
  // サイズ**を使う（一律 56×60 と見なすと過剰に団子へ落ちる＝r2 I3）。アンカーは下端中央。
  function markerFootprint(markers) {
    var width = 0;
    var height = 0;
    markers.forEach(function (marker) {
      var icon = marker.options && marker.options.icon;
      var size = icon && icon.options && icon.options.iconSize;
      width = Math.max(width, (size && size[0]) || MARKER_MAX_WIDTH);
      height = Math.max(height, (size && size[1]) || MARKER_MAX_HEIGHT);
    });
    return { width: width || MARKER_MAX_WIDTH, height: height || MARKER_MAX_HEIGHT };
  }

  // markercluster が実際に置く脚の矩形を再現する。円の中心はハブより 10px 下、脚長には 35px の
  // 下限がある。
  GeoMap.prototype.spiderLegRects = function (hub, count, radius, footprint) {
    var rects = [];
    var centerY = hub.y + SPIDER_CENTER_OFFSET_Y;
    var length = Math.max(radius, SPIDER_MIN_LEG_LENGTH);
    var step = 2 * Math.PI / count;
    for (var i = 0; i < count; i += 1) {
      var x = hub.x + length * Math.cos(i * step);
      var y = centerY + length * Math.sin(i * step);
      rects.push({
        left: x - footprint.width / 2,
        right: x + footprint.width / 2,
        top: y - footprint.height,
        bottom: y
      });
    }
    return rects;
  };

  // 展開しない周囲の描画物（他店のピン・他のクラスタ・現在地マーカー）の矩形。
  // **これから消えるものは数えない**: 別クラスタが spiderfy 中なら、その脚のピンも線も
  // `cluster.spiderfy()` の冒頭の `_unspiderfy()` で畳まれる（r2 I1）。
  // 脚の線をクラス名で除外するのは不可（`leaflet-cluster-spider-leg` は**アニメーション版の
  // 経路でしか付かない**。reduced-motion では非アニメ版が動き、長い斜めの線の bbox が
  // 巨大な障害物になって常に団子へ落ちる＝r2 I2）。SVG は現在地マーカーだけを明示的に拾う。
  GeoMap.prototype.otherMarkerRects = function (cluster, children) {
    var skip = children.map(function (marker) { return marker._icon; });
    var spiderfied = this.clusterGroup._spiderfied;
    if (spiderfied) {
      skip.push(spiderfied._icon);
      spiderfied.getAllChildMarkers().forEach(function (marker) { skip.push(marker._icon); });
    }
    var rects = [];
    var nodes = [].slice.call(this.canvas.querySelectorAll('.leaflet-marker-icon'));
    if (this.locationMarker && this.locationMarker._path) nodes.push(this.locationMarker._path);
    nodes.forEach(function (node) {
      if (node === cluster._icon || skip.indexOf(node) !== -1) return;
      var rect = node.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      rects.push({ left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom });
    });
    return rects;
  };

  // canvas の上に載っている UI。脚がこの下に入るとタップ領域が塞がれる。
  // **canvas に重なる絶対配置の UI をここに全部並べる**のが規約（r1 W2 / r2 C1）。
  // 新しいオーバーレイを足したらこの定数にも足すこと。`.geo-map__close` は z-index 1002 で
  // marker pane より前面にあり、脚が下に入ると「店を選んだつもりが地図が閉じる」になる。
  // status（live region）だけは含めない。既定の下端余白 24px より背が高く、通常のピンも
  // 今の実装では重なり得るため、脚にだけ厳しくすると一貫しない。
  var OVERLAY_SELECTOR = '[data-geo-map-title], [data-geo-map-close], [data-geo-map-controls], .leaflet-control';

  GeoMap.prototype.overlayRects = function () {
    var rects = [];
    Array.prototype.forEach.call(this.root.querySelectorAll(OVERLAY_SELECTOR), function (node) {
      if (node.hidden) return;
      var rect = node.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      rects.push({ left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom });
    });
    return rects;
  };

  // **パンを当てにしない**。spiderfy 後の `panSpiderIntoView` は 'spiderfied' の発火が脚の
  // 位置確定と競合するため発火しないことがある（渋谷の実測で offset=0 のまま脚が canvas 外に
  // 残った）。収容を不変条件にする以上、「パンすれば入る」を根拠に展開してはいけない。
  GeoMap.prototype.spiderPlacementFits = function (hub, count, radius, area, movable, fixed, footprint) {
    var legs = this.spiderLegRects(hub, count, radius, footprint);
    for (var i = 0; i < legs.length; i += 1) {
      if (!rectInside(legs[i], area, MIN_MARKER_GAP)) return false;
      for (var j = i + 1; j < legs.length; j += 1) {
        if (rectsCollide(legs[i], legs[j], MIN_MARKER_GAP)) return false;
      }
      for (var k = 0; k < movable.length; k += 1) {
        if (rectsCollide(legs[i], movable[k], MIN_MARKER_GAP)) return false;
      }
      for (var f = 0; f < fixed.length; f += 1) {
        if (rectsCollide(legs[i], fixed[f], MIN_MARKER_GAP)) return false;
      }
    }
    return true;
  };

  // spiderfy が「dialog に収まる」「脚どうし・ハブ・周囲のマーカー・canvas 上の UI と
  // 重ならない」を満たせるか。
  //
  // **判定は式でなく実際の脚の矩形で行う**（r1 C1）。角度ごとに効く制約が変わる（真下の脚は
  // ピンの高さ 60px と円中心の +10px でハブと重なり、真横の脚は幅で決まる）ため、
  // 1 つの閉じた式で表そうとすると n によって崩れる。
  //
  // **倍率は既定の 5.2 固定**。設計 §10-12-4 は「収まらなければその cluster だけ縮める」と
  // していたが、実配置では縮小が効く帯域がほぼ無い（縮めると収容・他店との距離は良くなるが
  // ハブとの重なりと脚どうしの間隔が悪化する。端のクラスタは fitBounds の余白線上に来るので
  // そもそも足りない）。変異テストでも縮小分岐へ到達する fixture を作れなかったため、
  // 未検証の経路を残さず落とした（r1 C1 の推奨どおり。§10-14-2）。
  GeoMap.prototype.spiderCanFit = function (cluster) {
    var children = cluster.getAllChildMarkers();
    var count = children.length;
    if (count < 2 || count >= SPIDER_SPIRAL_SWITCHOVER) return false;
    if (!cluster._icon) return false;

    // 収容の枠は canvas そのもの。fitBounds の余白（EDGE_PADDING_*）をここへ流用すると、
    // bounds の端にあるクラスタは構造的に必ず余白へ食い込むので spiderfy が原理的に成立しない。
    // 代わりに、canvas に重なって載っている UI（タイトル・ズームコントロール・status・
    // attribution）を**障害物として実測**する（r1 W2 の趣旨＝脚がそれらの下へ入らないこと）。
    var area = this.canvas.getBoundingClientRect();
    var hubRect = cluster._icon.getBoundingClientRect();
    var hub = { x: (hubRect.left + hubRect.right) / 2, y: (hubRect.top + hubRect.bottom) / 2 };
    var movable = [{ left: hubRect.left, right: hubRect.right, top: hubRect.top, bottom: hubRect.bottom }]
      .concat(this.otherMarkerRects(cluster, children));
    var fixed = this.overlayRects();

    var radius = SPIDER_FOOT_SEPARATION * SPIDERFY_DISTANCE_MULTIPLIER * (2 + count) / (2 * Math.PI);
    return this.spiderPlacementFits(hub, count, radius, area, movable, fixed, markerFootprint(children));
  };

  // 収まるときだけ spiderfy する。
  GeoMap.prototype.spiderfyIfFits = function (cluster, targets) {
    if (!this.spiderCanFit(cluster)) return Promise.resolve('clustered');

    return this.spiderfyAndWait(cluster, targets).then(function (expanded) {
      return expanded ? 'spiderfied' : 'partial';
    });
  };

  // §10-12-4 の有限ループ本体。**収容（街の全店が視界に入っていること）を不変条件**にし、
  // クラスタ中心へ寄る setView は使わない。各反復は return するか zoom を必ず 1 上げ、
  // さらに getBoundsZoom の天井と MAX_EXPAND_STEPS で二重に有限。
  GeoMap.prototype.expandMarkers = function (targets, onInitialFit) {
    var self = this;
    var L = window.L;
    var generation = ++this.expandGeneration;
    // spiderfy 済みクラスタのハブをタップしたとき、子は脚の座標を持っている（markercluster が
    // 実座標を _preSpiderfyLatlng へ退避して setLatLng する）。実座標で bounds を組む（r1 W9）。
    var points = targets.map(function (marker) { return marker._preSpiderfyLatlng || marker.getLatLng(); });
    var bounds = L.latLngBounds(points);
    var attempts = 0;

    this.root.setAttribute('data-geo-map-expand-steps', '0');
    // 前の街の視界を読ませない（oracle が古い値で通るのを防ぐ＝r2 I4）。
    this.root.removeAttribute('data-geo-map-bounds');
    return this.moveAndWait(function () {
      self.map.fitBounds(bounds, self.fitPadding({ maxZoom: FIT_BOUNDS_MAX_ZOOM }));
    }).then(function () {
      self.root.setAttribute('data-geo-map-fit-zoom', String(self.map.getZoom()));
      if (onInitialFit) onInitialFit();
      if (generation !== self.expandGeneration) return null;

      var runStep = function (step) {
        if (generation !== self.expandGeneration) return Promise.resolve(null);
        var state = self.clustersCoveringTargets(targets);

        if (step > MAX_EXPAND_STEPS) {
          if (!state.clusters.length) return Promise.resolve(state.offscreen ? 'partial' : 'separated');
          return self.spiderfyIfFits(state.clusters[0], targets);
        }

        attempts += 1;
        self.root.setAttribute('data-geo-map-expand-steps', String(attempts));
        // 収容が破れているときは separated を名乗らない（画面外の店は「分離できた」ではない）。
        if (state.offscreen) return Promise.resolve('partial');
        if (state.clusters.length === 0) return Promise.resolve('separated');
        if (state.clusters.length >= 2) return Promise.resolve('partial');

        var cluster = state.clusters[0];
        // 完全同一座標はどれだけ寄っても割れないので spiderfy 一択。ただし「収まるか」の
        // 検査は必ず通す（通さないと脚が canvas 外へ出たまま spiderfied を申告する＝r1 C3）。
        if (self.samePoint(cluster.getAllChildMarkers())) {
          return self.spiderfyIfFits(cluster, targets);
        }

        // 収容を保ったまま 1 段だけ寄る。fitBounds なので視界は必ず対象全体を含み続ける。
        var nextZoom = self.map.getZoom() + 1;
        if (self.containableZoom(bounds) >= nextZoom) {
          return self.moveAndWait(function () {
            self.map.fitBounds(bounds, self.fitPadding({ maxZoom: nextZoom }));
          }).then(function () { return runStep(step + 1); });
        }

        // 収容の天井。これ以上寄ると誰かが画面外へ出るので、分離は spiderfy に委ねる。
        return self.spiderfyIfFits(cluster, targets);
      };

      return runStep(1);
    });
  };

  GeoMap.prototype.expandForLocation = function (locationId, onInitialFit, targetScope) {
    var targets = this.markersForLocation(locationId, targetScope);
    if (!targets.length) return Promise.resolve(null);
    // 街単位はクラスタを持たないので、ズームループでなく置き直しで分離する（§15）。
    if (this.declutterScope()) return this.placeMarkers(targets, onInitialFit);

    return this.expandMarkers(targets, onInitialFit);
  };

  // クラスタを直接タップ／Enter したときも、同じ有限ループ本体を使う。**街の一部だけを映す**ので、
  // 街全体を展開したかのように読み上げてはいけない（r2 W5）。
  //
  // `spiderfyOnMaxZoom` を false にしてライブラリ側の同期 spiderfy が無くなったぶん、
  // ここが唯一の展開経路になるので取りこぼせない（`clusterclick` と `clusterkeypress` の両方を
  // 自前で受ける必要がある）。
  //
  // **Enter が `clusterclick` も発火させるという以前の記述は誤りだった**（§12-2）。
  // `clusterkeypress` のハンドラを外すと Enter 経路が完全に死ぬことを変異テストで確認している。
  // `pendingCluster` のガードは二重発火対策ではなく、**展開中の再タップ**への防御として残す。
  GeoMap.prototype.expandCluster = function (cluster) {
    var self = this;
    if (this.pendingCluster === cluster) return;
    this.pendingCluster = cluster;

    var targets = cluster.getAllChildMarkers();
    var label = this.targetLabelForMarkers(targets);
    this.expandMarkers(targets).then(function (result) {
      if (!result) return;
      self.announce(self.expansionMessage(result, label, { partOfTarget: true }));
      // 深掘りできたかを計測で追えるようにする（r1 W4 / r2 W4）。イベント名は登録済みの
      // ものを使い回し、自動展開と `trigger` で区別する（新しい名前は GTM 登録まで GA4 へ届かない）。
      pushDataLayer({
        event: 'location_map_expand_result',
        surface: self.scope,
        surfaceType: self.surfaceType,
        locationId: self.activeTargetScope === 'prefecture' ? '' : String(self.activeLocationId || ''),
        scope: self.activeTargetScope,
        result: result,
        trigger: 'cluster_tap'
      });
    }).catch(function (error) {
      self.failOpen(error);
    }).then(function () {
      if (self.pendingCluster === cluster) self.pendingCluster = null;
    });
  };

  GeoMap.prototype.targetLabelForMarkers = function (markers) {
    var locationIds = [];
    markers.forEach(function (marker) {
      var id = num(marker.geoMapLocationId);
      if (locationIds.indexOf(id) === -1) locationIds.push(id);
    });
    if (locationIds.length === 1 && locationIds[0] === this.activeLocationId && this.activeTargetLabel) {
      return this.activeTargetLabel;
    }
    return this.scope;
  };

  GeoMap.prototype.expansionMessage = function (result, targetLabel, options) {
    var label = targetLabel || this.scope;
    if (options && options.partOfTarget) label += 'のこの付近';
    if (result === 'separated') return label + 'の店舗を個別のピンで表示しました。';
    // 重なる店は少しずらして置いた。ずらした事実を視覚（引き出し線）だけで伝えない。
    if (result === 'displaced') {
      return label + 'の店舗を個別のピンで表示しました。重なる店舗は少しずらし、' +
        '実際の位置を細い線と点で示しています。';
    }
    if (result === 'partial') return label + 'の店舗の位置関係を表示しました。一部はまとめて表示しています。';
    // 収容を優先して団子のまま置いた。タップすれば内訳を開けることまで伝える。
    if (result === 'clustered') {
      return label + 'の店舗をまとめて表示しました。アイコンを選ぶと内訳を開けます。';
    }
    return label + 'の重なる店舗を個別のピンに展開しました。';
  };

  // 寄せる対象は **今 spiderfy した子マーカーだけ**。canvas 上の全マーカーを見ると、
  // 対象外の街のピン（視界の外だが markercluster が `getBounds().pad(1)` の内側は DOM に残す）
  // に引きずられ、**収容という不変条件を壊してまで**そちらへ寄せてしまう。
  // 実測: 中心の西 200px の同一座標ペアを spiderfy したとき、東 1020px にある別の街の 1 店へ
  // 約 450px 寄せて対象を視界の外へ出し、'spiderfied' のはずが 'partial' に落ちた。
  GeoMap.prototype.panSpiderIntoView = function (markers) {
    if (this.panning) return;
    if (!markers || !markers.length) return;

    var box = this.canvas.getBoundingClientRect();
    var min = { x: Infinity, y: Infinity };
    var max = { x: -Infinity, y: -Infinity };

    markers.forEach(function (marker) {
      var node = typeof marker.getElement === 'function' ? marker.getElement() : null;
      if (!node) return;

      var rect = node.getBoundingClientRect();
      if (!rect.width) return;
      min.x = Math.min(min.x, rect.left);
      min.y = Math.min(min.y, rect.top);
      max.x = Math.max(max.x, rect.right);
      max.y = Math.max(max.y, rect.bottom);
    });
    if (!isFinite(min.x)) return;

    // 端ぴったりではなく 4px 内側まで寄せる（spec が要求する収容の余白と同じ値）。
    // なお spiderCanFit はこのパンを当てにしない（'spiderfied' の発火と脚の位置確定が
    // 競合して発火しないことがある＝§10-14-1）。ここは best-effort の寄せに留める。
    var offset = [
      axisOffset(min.x, max.x, box.left + MIN_MARKER_GAP, box.right - MIN_MARKER_GAP),
      axisOffset(min.y, max.y, box.top + MIN_MARKER_GAP, box.bottom - MIN_MARKER_GAP)
    ];
    if (offset[0] === 0 && offset[1] === 0) return;

    this.panning = true;
    var self = this;
    this.map.once('moveend', function () { self.panning = false; });
    this.map.panBy(offset, { animate: !reducedMotion() });
  };

  // `compact` は街単位の地図（クラスタを出さず全店を個別に置く面）。
  // 同じ marker を両方の面で使い回すことは無い（scope は面ごとに排他＝§15-3）。
  GeoMap.prototype.buildMarker = function (branch, compact) {
    var self = this;
    var L = window.L;
    var lat = Number(branch.lat);
    var lng = Number(branch.lng);
    if (!isFinite(lat) || !isFinite(lng)) return null;

    var gender = !branch.status_label && (branch.is_number || branch.is_percent_gender);
    var size = compact ? [COMPACT_PIN_WIDTH, COMPACT_PIN_HEIGHT] : (gender ? [56, 60] : [48, 52]);
    // 通常ピンは下端中央（矢印が指す）、小さいチップは中心。
    var anchor = compact ? [size[0] / 2, size[1] / 2] : [size[0] / 2, size[1]];
    var icon = L.divIcon({
      className: 'map-marker',
      html: compact ? compactPinHtml(branch) : pinHtml(branch),
      iconSize: size,
      iconAnchor: anchor,
      popupAnchor: [0, -anchor[1] + 4]
    });

    var marker = L.marker([lat, lng], { icon: icon, keyboard: true });
    marker.geoMapLocationId = num(branch.location_id);
    // **真の座標は専用フィールドへ持つ**。ずらした後も `getLatLng()` は真の座標のままにする
    // （markercluster の `_preSpiderfyLatlng` を読む既存コードと意味を二重化しない＝§15-4）。
    marker.geoMapTrueLatLng = L.latLng(lat, lng);
    marker.geoMapAnchor = anchor;
    marker.geoMapBranchId = num(branch.id);
    marker.bindPopup(function () { return popupNode(branch); });

    marker.on('add', function () {
      var element = marker.getElement();
      if (!element) return;
      element.setAttribute('role', 'button');
      element.setAttribute('aria-label', accessibleName(branch));
      element.setAttribute('data-gtm-event', 'location_map_pin_click');
      element.setAttribute('data-gtm-branch-id', String(num(branch.id)));
    });
    marker.on('popupopen', function () {
      pushDataLayer({
        event: 'location_map_popup_open',
        surface: self.scope,
        surfaceType: self.surfaceType,
        branchId: String(num(branch.id))
      });
    });

    return marker;
  };

  GeoMap.prototype.labelClusters = function () {
    var nodes = this.canvas.querySelectorAll('.geo-map__cluster-icon');
    Array.prototype.forEach.call(nodes, function (node) {
      var count = num((node.textContent || '').replace(/[^0-9]/g, ''));
      node.setAttribute('role', 'button');
      node.setAttribute('aria-label', 'この付近の' + count + '店舗。開くと個別のピンに分かれます');
    });
  };

  GeoMap.prototype.locate = function () {
    var self = this;
    if (!navigator.geolocation) {
      this.announce('この端末では現在地を取得できません。');
      return;
    }

    this.announce('現在地を取得しています…');
    navigator.geolocation.getCurrentPosition(function (position) {
      var L = window.L;
      var latlng = [position.coords.latitude, position.coords.longitude];
      if (self.locationMarker) {
        self.locationMarker.setLatLng(latlng);
      } else {
        self.locationMarker = L.circleMarker(latlng, {
          radius: 8, color: '#ffffff', weight: 3, fillColor: '#4285f4', fillOpacity: 1,
          className: 'geo-map__current-location'
        }).addTo(self.map);
      }
      var inside = self.map.getBounds().contains(L.latLng(latlng));
      self.announce(inside ? '現在地を地図に表示しました。' : '現在地は表示中の範囲の外です。地図を動かすと確認できます。');
    }, function () {
      self.announce('現在地を取得できませんでした。');
    });
  };

  // 入口の露出を分母にする。街ボタンごとに1回だけ発火する。
  GeoMap.prototype.observeLocationButtons = function () {
    var self = this;
    var buttons = Array.prototype.slice.call(this.locationButtons());
    var fire = function (button) {
      if (button.getAttribute('data-geo-map-viewed') === 'true') return;
      button.setAttribute('data-geo-map-viewed', 'true');
      pushDataLayer({
        event: 'location_map_view',
        surface: self.scope,
        surfaceType: self.surfaceType,
        locationId: button.getAttribute('data-geo-map-location-id') || '',
        scope: button.getAttribute('data-geo-map-target-scope') || 'location'
      });
    };

    if (!('IntersectionObserver' in window)) {
      buttons.forEach(fire);
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        fire(entry.target);
      });
    }, { threshold: 0.25 });
    buttons.forEach(function (button) { observer.observe(button); });
  };

  GeoMap.prototype.openForLocation = function (button) {
    var self = this;
    var targetScope = button.getAttribute('data-geo-map-target-scope') || 'location';
    var rawLocationId = button.getAttribute('data-geo-map-location-id') || '';
    var locationId = targetScope === 'prefecture' ? null : num(rawLocationId);
    var targetLabel = button.getAttribute('data-geo-map-location-name') || this.scope;
    var branchCount = num(button.getAttribute('data-geo-map-branch-count'));
    if (!this.branchesForLocation(locationId, targetScope).length) return;

    this.activeLocationId = locationId;
    this.activeTargetScope = targetScope;
    this.activeTargetLabel = targetLabel;
    this.opener = button;
    this.scrollPosition = { x: window.scrollX, y: window.scrollY };
    this.openGeneration += 1;
    var generation = this.openGeneration;

    if (this.map) this.map.closePopup();
    if (this.title) this.title.textContent = targetLabel + 'の' + branchCount + '店を地図で見る';
    this.setButtonLoading(button, true);
    this.announce('地図を読み込んでいます…');
    // **収容判定より前に出しておく**。展開の完了後に出すと、判定時点では hidden で
    // 障害物に数えられず、しかも閉じても hidden へ戻さないので「同じ街の 1 回目と 2 回目で
    // spiderfied / clustered が入れ替わる」非決定性になる（r2 C2）。
    if (this.controls) this.controls.hidden = false;
    this.dialog.showModal();
    this.lockBackground();
    this.pushHistory();

    this.ensureAssets().then(function () {
      if (!self.dialog.open || generation !== self.openGeneration) return null;
      if (!self.map) self.render();

      return new Promise(function (resolve, reject) {
        window.requestAnimationFrame(function () {
          if (!self.dialog.open || generation !== self.openGeneration) {
            resolve(null);
            return;
          }
          try {
            // dialog を showModal() した後で必ずサイズを再計算する。
            self.map.invalidateSize();
            resolve(self.expandForLocation(locationId, function () {
              if (!self.dialog.open || generation !== self.openGeneration) return;
              pushDataLayer({
                event: 'location_map_render_success',
                surface: self.scope,
                surfaceType: self.surfaceType,
                locationId: rawLocationId,
                scope: targetScope
              });
            }, targetScope));
          } catch (error) {
            reject(error);
          }
        });
      });
    }).then(function (result) {
      if (!result || !self.dialog.open || generation !== self.openGeneration) return;
      self.announce(self.expansionMessage(result, targetLabel));
      pushDataLayer({
        event: 'location_map_expand_result',
        surface: self.scope,
        surfaceType: self.surfaceType,
        locationId: rawLocationId,
        scope: targetScope,
        result: result,
        trigger: 'auto'
      });
    }).catch(function (error) {
      if (generation === self.openGeneration) self.failOpen(error);
    }).then(function () {
      self.setButtonLoading(button, false);
    });
  };

  GeoMap.prototype.start = function () {
    var self = this;

    if (!this.usable()) {
      Array.prototype.forEach.call(this.locationButtons(), function (button) { button.hidden = true; });
      return;
    }

    this.observeLocationButtons();

    document.addEventListener('click', function (event) {
      var popupLink = event.target.closest && event.target.closest('.geo-map__popup-link');
      if (popupLink) {
        self.navigateFromPopup(popupLink, event);
        return;
      }
      var button = event.target.closest && event.target.closest('[data-geo-map-location-button]');
      if (button) self.openForLocation(button);
    });

    var closeButton = this.dialog.querySelector('[data-geo-map-close]');
    if (closeButton) closeButton.addEventListener('click', function () { self.closeDialog(false); });
    this.dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      self.closeDialog(false);
    });

    window.addEventListener('popstate', function () {
      if (self.pendingNavigationUrl) {
        var url = self.pendingNavigationUrl;
        self.pendingNavigationUrl = null;
        clearTimeout(self.pendingNavigationTimer);
        self.finishNavigationFromDialog(url, false);
        return;
      }
      if (self.historyBackPending) {
        self.historyBackPending = false;
        return;
      }
      if (self.dialog.open && self.historyEntryActive) self.closeDialog(true);
    });

    var locateButton = this.dialog.querySelector('[data-geo-map-locate]');
    if (locateButton) locateButton.addEventListener('click', function () { self.locate(); });
  };

  function init() {
    var root = document.querySelector('[data-geo-map]');
    if (!root) return;
    if (root.getAttribute('data-geo-map-initialized') === 'true') return;
    root.setAttribute('data-geo-map-initialized', 'true');
    new GeoMap(root).start();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
